import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../store";
import {
  updateWarrantyDetails,
  clearError,
} from "../../store/slices/warrantyDetailsSlice";
import { fetchProductsList } from "../../store/slices/productSlice";
import { getAllEpcNamesIds } from "../../store/slices/epcSlice";
import { toast } from "react-toastify";
import { WarrantyDetails } from "../../models/warrantlyDetails.model";
import Select from "react-select";
import { getStatesData } from "../../store/slices/kanbanSlice";

interface WarrantyDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  warrantyId: string;
}

const WarrantyDetailsModal: React.FC<WarrantyDetailsModalProps> = ({
  isOpen,
  onClose,
  warrantyId,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const warrantyDetails = useSelector((state: RootState) =>
    state.warrantyDetails.warrantyDetails.find(
      (item) => item._id === warrantyId
    )
  );
  const products = useSelector(
    (state: RootState) => state.product.products || []
  );
  const { epc: epcs } = useSelector((state: RootState) => state.epc);
  const { statesData } = useSelector((state: RootState) => state.kanban);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<WarrantyDetails>>({});
  const [stateOptions, setStateOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [districtOptions, setDistrictOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [cityOptions, setCityOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchProductsList());
      dispatch(getStatesData());
      dispatch(clearError());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (warrantyDetails) {
      setFormData({
        ...warrantyDetails,
        purchaseDate: warrantyDetails.purchaseDate
          ? new Date(warrantyDetails.purchaseDate).toISOString().split("T")[0]
          : "",
        warrantyEndDate: warrantyDetails.warrantyEndDate
          ? new Date(warrantyDetails.warrantyEndDate)
              .toISOString()
              .split("T")[0]
          : "",
      });
    }
  }, [warrantyDetails]);

  useEffect(() => {
    if (isOpen && statesData) {
      // Populate state options
      const uniqueStates = statesData.map((s) => ({
        value: s.state,
        label: s.state,
      }));
      setStateOptions(uniqueStates);

      // If formData has a state, filter districts
      if (formData.state && typeof formData.state === "string") {
        const filteredState = statesData.find(
          (s) => s.state === formData.state
        );
        if (filteredState) {
          const uniqueDistricts = Array.from(
            new Set(filteredState.districts)
          ).map((district) => ({ value: district, label: district }));
          setDistrictOptions(uniqueDistricts);

          // If formData has a district, filter cities
          if (formData.district && typeof formData.district === "string") {
            const uniqueCities = Array.from(new Set(filteredState.city))
              .filter((city) =>
                filteredState.districts.includes(formData.district!)
              )
              .map((city) => ({ value: city, label: city }));
            setCityOptions(uniqueCities);
          } else {
            setCityOptions([]);
          }
        } else {
          setDistrictOptions([]);
          setCityOptions([]);
        }
      } else {
        setDistrictOptions([]);
        setCityOptions([]);
      }
    }
  }, [isOpen, formData.state, formData.district, statesData]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !warrantyDetails) return null;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (option: any, field: keyof WarrantyDetails) => {
    const newFormData = { ...formData, [field]: option ? option.value : "" };

    if (field === "product" && option) {
      const selectedProduct = products.find(
        (p) => p.productName === option.value
      );
      newFormData["productId"] = selectedProduct ? selectedProduct._id : "";
    } else if (field === "epcName" && option) {
      const selectedEpc = epcs.find((e) => e.name === option.value);
      newFormData["epcId"] = selectedEpc ? selectedEpc._id : "";
    }

    if (field === "state" && statesData) {
      const selectedState =
        option && typeof option.value === "string" ? option.value : "";
      if (selectedState) {
        const filteredState = statesData.find((s) => s.state === selectedState);
        if (filteredState) {
          const uniqueDistricts = Array.from(
            new Set(filteredState.districts)
          ).map((district) => ({ value: district, label: district }));
          setDistrictOptions(uniqueDistricts);
          setCityOptions([]);
          newFormData.district = "";
          newFormData.city = "";
        } else {
          setDistrictOptions([]);
          setCityOptions([]);
          newFormData.district = "";
          newFormData.city = "";
        }
      } else {
        setDistrictOptions([]);
        setCityOptions([]);
        newFormData.district = "";
        newFormData.city = "";
      }
    } else if (field === "district" && statesData) {
      const selectedDistrict =
        option && typeof option.value === "string" ? option.value : "";
      const selectedState =
        newFormData.state && typeof newFormData.state === "string"
          ? newFormData.state
          : formData.state && typeof formData.state === "string"
          ? formData.state
          : "";
      if (selectedDistrict && selectedState) {
        const filteredState = statesData.find((s) => s.state === selectedState);
        if (filteredState) {
          const uniqueCities = Array.from(new Set(filteredState.city))
            .filter((city) =>
              filteredState.districts.includes(selectedDistrict)
            )
            .map((city) => ({ value: city, label: city }));
          setCityOptions(uniqueCities);
          newFormData.city = "";
        } else {
          setCityOptions([]);
          newFormData.city = "";
        }
      } else {
        setCityOptions([]);
        newFormData.city = "";
      }
    }

    setFormData(newFormData);
  };

  const handleUpdate = () => {
    dispatch(updateWarrantyDetails({ _id: warrantyId, data: formData }))
      .unwrap()
      .then(() => {
        toast.success("Warranty details updated successfully");
        setIsEditing(false);
        onClose();
      })
      .catch((error) => {
        toast.error(`Failed to update warranty details: ${error}`);
      });
  };

  const getFieldLabel = (field: string): string => {
    const labels: { [key: string]: string } = {
      sn: "Serial Number",
      epcName: "EPC Name",
      mobileNumber: "Mobile Number",
      pincode: "PIN Code",
      purchaseDate: "Purchase Date",
      warrantyEndDate: "Warranty End Date",
    };
    return labels[field] || field.replace(/([A-Z])/g, " $1").trim();
  };

  const productOptions = Object.values(products).map((product) => ({
    value: product.productName,
    label: product.productName || "Unknown Product",
  }));

  const epcOptions = epcs.map((epc) => ({
    value: epc.name,
    label: epc.name || "Unknown EPC",
  }));

  const fields: Array<keyof WarrantyDetails> = [
    "name",
    "email",
    "mobileNumber",
    "sn",
    "product",
    "epcName",
    "address",
    "state",
    "district",
    "city",
    "pincode",
    "purchaseDate",
    "warrantyEndDate",
  ];

  return (
    <div className="fixed inset-0 bg-gray-700 bg-opacity-60 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col transform transition-transform duration-300 scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-gray-100 to-gray-200 px-6 py-3 border-b border-gray-300 shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Warranty Details
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`p-3 rounded-xl transition-all duration-200 ${
                  isEditing
                    ? "bg-blue-100 text-blue-600 shadow-inner"
                    : "text-gray-600 hover:bg-gray-300 hover:text-gray-800 shadow-sm"
                }`}
                title={isEditing ? "Cancel Editing" : "Edit Details"}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
              <button
                onClick={onClose}
                className="p-3 text-gray-600 hover:bg-gray-300 hover:text-gray-800 rounded-xl shadow-sm transition-all duration-200"
                title="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-gray-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {fields.map((field) => (
              <div
                key={field}
                className={`${
                  isEditing
                    ? "bg-white p-4 rounded-lg shadow-sm border border-gray-200"
                    : "bg-white p-4 rounded-lg shadow-sm border border-gray-200"
                } transition-all duration-200 ${
                  field === "address" && isEditing ? "lg:col-span-2" : ""
                }`}
              >
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  {getFieldLabel(field)}
                </label>
                {isEditing ? (
                  field === "product" ? (
                    <Select
                      options={productOptions}
                      value={productOptions.find(
                        (option) => option.value === formData.product
                      )}
                      onChange={(option) =>
                        handleSelectChange(option, "product")
                      }
                      placeholder="Select Product"
                      isClearable
                      isSearchable
                      className="text-sm"
                      classNamePrefix="react-select"
                    />
                  ) : field === "epcName" ? (
                    <Select
                      options={epcOptions}
                      value={epcOptions.find(
                        (option) => option.value === formData.epcName
                      )}
                      onChange={(option) =>
                        handleSelectChange(option, "epcName")
                      }
                      placeholder="Select EPC"
                      isClearable
                      isSearchable
                      className="text-sm"
                      classNamePrefix="react-select"
                    />
                  ) : field === "state" ? (
                    <Select
                      options={stateOptions}
                      value={stateOptions.find(
                        (option) => option.value === formData.state
                      )}
                      onChange={(option) => handleSelectChange(option, "state")}
                      placeholder="Select State"
                      isClearable
                      isSearchable
                      className="text-sm"
                      classNamePrefix="react-select"
                    />
                  ) : field === "district" ? (
                    <Select
                      options={districtOptions}
                      value={districtOptions.find(
                        (option) => option.value === formData.district
                      )}
                      onChange={(option) =>
                        handleSelectChange(option, "district")
                      }
                      placeholder="Select District"
                      isClearable
                      isSearchable
                      className="text-sm"
                      classNamePrefix="react-select"
                    />
                  ) : field === "city" ? (
                    <Select
                      options={cityOptions}
                      value={cityOptions.find(
                        (option) => option.value === formData.city
                      )}
                      onChange={(option) => handleSelectChange(option, "city")}
                      placeholder="Select City"
                      isClearable
                      isSearchable
                      className="text-sm"
                      classNamePrefix="react-select"
                    />
                  ) : field === "address" ? (
                    <textarea
                      name={field}
                      value={formData[field] || ""}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                    />
                  ) : (
                    <input
                      type={field.includes("Date") ? "date" : "text"}
                      name={field}
                      value={formData[field] || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                    />
                  )
                ) : (
                  <p className="text-sm text-gray-800 mt-1 leading-relaxed">
                    {field.includes("Date") &&
                    warrantyDetails[field] &&
                    !isNaN(new Date(warrantyDetails[field]).getTime())
                      ? new Date(warrantyDetails[field]).toLocaleDateString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )
                      : warrantyDetails[field] || (
                          <span className="text-gray-400 italic">
                            Not provided
                          </span>
                        )}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gradient-to-r from-gray-100 to-gray-200 px-6 py-3 border-t border-gray-300 shadow-inner">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="text-xs text-gray-600">
              {isEditing
                ? "Make your changes and click update to save"
                : "Click the edit icon to modify details"}
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-none px-6 py-2.5 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm font-medium transition-all duration-200 shadow-sm"
              >
                Close
              </button>
              {isEditing && (
                <button
                  onClick={handleUpdate}
                  className="flex-1 sm:flex-none px-6 py-2.5 bg-[#0077C0] text-white rounded-lg hover:bg-[#005B90] text-sm font-medium transition-all duration-200 shadow-sm"
                >
                  Update Details
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarrantyDetailsModal;
